// src/component/page/album/HiveMining.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Stack,
  Chip,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { getCookie } from "../../../utils/getCookie";
import { useSelector } from "react-redux";

// one day in ms (for the mining animation)
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// life threshold to consider miner broken (must match backend)
const BROKEN_THRESHOLD = 10;

// helper to compute daily/weekly preview for a miner
function computeMiningPreview(miner, now) {
  if (!miner) {
    return {
      dailyPreview: 0,
      fractionDay: 0,
      weeklyPreview: 0,
      weeklyFraction: 0,
    };
  }

  const isBroken = !miner.active || Number(miner.lifeCurrent || 0) <= BROKEN_THRESHOLD;

  const dailyGoal = Number(miner.dailyGoal ?? miner.dailyProduction ?? 0);
  const weeklyGoal = Number(miner.weeklyGoal ?? dailyGoal * 7);
  const baseWeekly = Number(miner.weeklyAccumulated || 0);

  // if miner is broken, or we don't have what we need, freeze progress
  if (!now || dailyGoal <= 0 || !miner.lastUpdateAt || isBroken) {
    const weeklyFraction =
      weeklyGoal > 0 ? Math.min(1, baseWeekly / weeklyGoal) : 0;

    return {
      dailyPreview: 0,
      fractionDay: 0,
      weeklyPreview: baseWeekly,
      weeklyFraction,
    };
  }

  const last = new Date(miner.lastUpdateAt).getTime();
  let elapsed = now - last;
  if (elapsed < 0) elapsed = 0;

  // only the progress inside the current day
  const withinDay = elapsed % ONE_DAY_MS;
  const fractionDay = Math.min(1, withinDay / ONE_DAY_MS);

  const dailyPreview = dailyGoal * fractionDay;
  const weeklyPreview = baseWeekly + dailyPreview;
  const weeklyFraction =
    weeklyGoal > 0 ? Math.min(1, weeklyPreview / weeklyGoal) : 0;

  return {
    dailyPreview,
    fractionDay,
    weeklyPreview,
    weeklyFraction,
  };
}

// same scheme as in ArenaPage.jsx
const URL_MAIN =
  process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:3002";

    
const ENDPOINTS = {
  myMiners: `${URL_MAIN}/api/mining/my`,
  config: `${URL_MAIN}/api/mining/config`,
  // 🔧 NUEVO: endpoint para reparar (ajusta si tu ruta es distinta)
  repair: `${URL_MAIN}/api/mining/repair`,
};

export const HiveMining = ({ onGoToMarket }) => {
  const [configs, setConfigs] = useState([]); // miner config (from backend)
  const [miners, setMiners] = useState([]); // user miners
  const [backendUser, setBackendUser] = useState(null); // user from /api/mining/my

  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadingMiners, setLoadingMiners] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [infoOpen, setInfoOpen] = useState(false);
  const [infoConfig, setInfoConfig] = useState(null);
  const [infoUserMiner, setInfoUserMiner] = useState(null);

  const [serverOffset, setServerOffset] = useState(0);
  const [now, setNow] = useState(Date.now());

  // 🔧 Estado para REPAIR
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairCfg, setRepairCfg] = useState(null);
  const [repairUserMiner, setRepairUserMiner] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairError, setRepairError] = useState("");
  const [repairPreviewHueso, setRepairPreviewHueso] = useState(0);

  const loading = loadingConfigs || loadingMiners;

  // user info from Redux (fallback if backend user is not present)
  const userInfo = useSelector((state) => state.usuarios?.informacion);

  const displayUserName =
    backendUser?.nombre ||
    userInfo?.nombre ||
    userInfo?.usuario ||
    userInfo?.user ||
    "";

  const displayUserHueso =
    backendUser != null
      ? Number(backendUser.dineroReal || 0)
      : Number(userInfo?.dineroReal || 0);

  // keep a ticking "now" based on server time
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now() + serverOffset);
    }, 1000);
    return () => clearInterval(id);
  }, [serverOffset]);

  // ---- load miner CONFIG ----
  const fetchConfigs = async () => {
    try {
      setLoadingConfigs(true);
      setErrorMsg("");

      const res = await axios.get(ENDPOINTS.config);
      if (!res.data?.ok) {
        setErrorMsg(res.data?.msg || "Error loading mining config.");
      } else {
        setConfigs(res.data.miners || []);
      }
    } catch (err) {
      console.error("HiveMining fetchConfigs error:", err);
      setErrorMsg("Error loading mining config.");
    } finally {
      setLoadingConfigs(false);
    }
  };

  // ---- load user miners ----
  const fetchMiners = async () => {
    try {
      setLoadingMiners(true);
      setErrorMsg("");

      const token = getCookie("token");
      if (!token) {
        setErrorMsg("You must be logged in to see your miners.");
        setLoadingMiners(false);
        return;
      }

      const res = await axios.get(ENDPOINTS.myMiners, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.data?.ok) {
        setErrorMsg(res.data?.msg || "Error loading miners.");
      } else {
        const serverTime = res.data.serverTime || Date.now();
        const clientNow = Date.now();
        setServerOffset(serverTime - clientNow);
        setNow(serverTime);

        setMiners(res.data.miners || []);
        setBackendUser(res.data.user || null);
      }
    } catch (err) {
      console.error("HiveMining fetchMiners error:", err);
      setErrorMsg("Error loading miners.");
    } finally {
      setLoadingMiners(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
    fetchMiners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // quick map by minerKey to bind config -> user miner
  const minersByKey = useMemo(() => {
    const map = new Map();
    for (const m of miners) {
      map.set(m.minerKey, m);
    }
    return map;
  }, [miners]);

  // total weekly mined (preview, including today's progress)
  const totalWeeklyVip = useMemo(() => {
    return miners.reduce((acc, m) => {
      const { weeklyPreview } = computeMiningPreview(m, now);
      return acc + weeklyPreview;
    }, 0);
  }, [miners, now]);

  const handleOpenInfo = (config) => {
    const userMiner = minersByKey.get(config.key) || null;
    setInfoConfig(config);
    setInfoUserMiner(userMiner);
    setInfoOpen(true);
  };

  const handleCloseInfo = () => {
    setInfoOpen(false);
    setInfoConfig(null);
    setInfoUserMiner(null);
  };

  const handleBuyClick = () => {
    if (onGoToMarket) {
      onGoToMarket(); // let Album switch to the market tab
    } else {
      // fallback: open secondary market in another tab
      window.open(
        "https://hivenfts.com/big-dog-bone/buy/",
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  // ======================
  //   HANDLERS REPAIR
  // ======================
  const handleOpenRepair = (cfg, userMiner) => {
    if (!userMiner) return;

    const lifeCurrent = Number(userMiner.lifeCurrent || 0);
    const lifeMax = Number(userMiner.lifeMax || 0);
    const missingLife = Math.max(0, lifeMax - lifeCurrent);

    // Coste estimado: usa cfg.repairCostPerLife si existe; si no, 0 (ajusta a tu gusto)
    const costPerLife = Number(cfg.repairCostPerLife || 0);
    const totalCost = costPerLife > 0 ? missingLife * costPerLife : 0;

    setRepairCfg(cfg);
    setRepairUserMiner(userMiner);
    setRepairPreviewHueso(totalCost);
    setRepairError("");
    setRepairOpen(true);
  };

  const handleCloseRepair = () => {
    if (repairLoading) return;
    setRepairOpen(false);
    setRepairCfg(null);
    setRepairUserMiner(null);
    setRepairPreviewHueso(0);
    setRepairError("");
  };

  const handleConfirmRepair = async () => {
    if (!repairCfg || !repairUserMiner) return;

    try {
      setRepairLoading(true);
      setRepairError("");

      const token = getCookie("token");
      if (!token) {
        setRepairError("You must be logged in to repair your miner.");
        setRepairLoading(false);
        return;
      }

      // Enviamos al backend el minerKey; el backend:
      //  - calcula el coste real
      //  - descuenta Hueso
      //  - repara/activa el miner
      const res = await axios.post(
        ENDPOINTS.repair,
        {
          nftId: repairUserMiner.nftId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.data?.ok) {
        setRepairError(res.data?.msg || "Error repairing miner.");
      } else {
        // Opcionalmente res.data.user, res.data.miner vienen actualizados.
        if (res.data.user) {
          setBackendUser(res.data.user);
        }
        // Lo más simple: recargamos todo el estado desde el backend
        await fetchMiners();
        handleCloseRepair();
      }
    } catch (err) {
      console.error("HiveMining repair error:", err);
      setRepairError("Error repairing miner.");
    } finally {
      setRepairLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 1.5, md: 2.5 },
        display: "flex",
        flexDirection: "column",
        color: "#fff",
        flexGrow: 1,
        minHeight: "calc(100vh - 140px)",
        pb: 4,
      }}
    >
      {/* Contenedor central para que no ocupe todo el ancho */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1000,
          mx: "auto",
        }}
      >
        {/* Header (compacto) */}
        <Box
          sx={{
            mb: 1.5,
            p: { xs: 1.2, md: 1.6 },
            borderRadius: 1.5,
            bgcolor: "rgba(0,0,0,0.55)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#ffd54f",
              textShadow: "0 0 6px rgba(0,0,0,0.8)",
              mb: 0.6,
              fontSize: { xs: "1.4rem", md: "1.6rem" },
            }}
          >
            ⛏️ Hive Ecosystem Mining
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#f5f5f5",
              maxWidth: 720,
              fontSize: { xs: "0.85rem", md: "0.9rem" },
            }}
          >
            Equip special NFTs from different games and communities to mine{" "}
            <strong>VIPHueso</strong> passively. Each miner has{" "}
            <strong>daily wear</strong> and <strong>weekly rewards</strong> that
            are sent directly to your Hive wallet every Sunday.
          </Typography>

          {/* header bottom: summary + user/hueso */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{
              mt: 1.2,
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={0.8}
              sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
            >
              <Chip
                size="small"
                label={
                  loading
                    ? "Calculating..."
                    : `Weekly mined (preview): ${totalWeeklyVip.toFixed(
                        3
                      )} VIPH`
                }
                sx={{
                  bgcolor: "rgba(255,213,79,0.2)",
                  border: "1px solid rgba(255,213,79,0.7)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 26,
                  px: 1.4,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "0.72rem",
                }}
              >
                Rewards are distributed once a week. Daily wear is applied
                automatically.
              </Typography>
            </Stack>

            {displayUserName && (
              <Stack
                direction="row"
                spacing={0.8}
                sx={{
                  mt: { xs: 0.8, md: 0 },
                  alignItems: "center",
                }}
              >
                <Chip
                  size="small"
                  label={displayUserName}
                  sx={{
                    bgcolor: "rgba(33,150,243,0.25)",
                    border: "1px solid rgba(33,150,243,0.7)",
                    color: "#E3F2FD",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 26,
                  }}
                />
                <Chip
                  size="small"
                  label={`${displayUserHueso.toLocaleString()} Hueso`}
                  sx={{
                    bgcolor: "rgba(255,213,79,0.25)",
                    border: "1px solid rgba(255,213,79,0.8)",
                    color: "#FFF8E1",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 26,
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Status messages */}
        {errorMsg && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity="warning"
              sx={{
                bgcolor: "rgba(255,244,179,0.1)",
                border: "1px solid rgba(255,213,79,0.4)",
                color: "#fff",
              }}
            >
              {errorMsg}
            </Alert>
          </Box>
        )}

        {/* Main grid */}
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: "transparent",
            border: "none",
          }}
        >
          {loading ? (
            <Box
              sx={{
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ color: "#fff" }}>
                Loading your miners...
              </Typography>
            </Box>
          ) : configs.length === 0 ? (
            <Box
              sx={{
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography variant="body1" sx={{ color: "#fff" }}>
                Mining config is not available yet.
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Please try again later.
              </Typography>
            </Box>
          ) : (
            <Grid
              container
              spacing={2.2}
              justifyContent="center"
              sx={{ px: { xs: 0.5, md: 1 } }}
            >
              {configs.map((cfg) => {
                const userMiner = minersByKey.get(cfg.key) || null;
                const hasMiner = !!userMiner;

                // 🔹 valores de vida y estados
                const lifeCurrent = hasMiner
                  ? Number(userMiner.lifeCurrent || 0)
                  : 0;
                const lifeMax = hasMiner
                  ? Number(userMiner.lifeMax || 0)
                  : 0;
                const isBroken =
                 hasMiner && lifeCurrent <= BROKEN_THRESHOLD; // roto si vida <= threshold
                 const isActive = hasMiner && !!userMiner.active && !isBroken;

                const isMining = isActive; // alias por claridad

                const lifePercent =
                  hasMiner && lifeMax > 0
                    ? Math.max(0, Math.min(100, (lifeCurrent / lifeMax) * 100))
                    : 0;

                const preview = hasMiner
                  ? computeMiningPreview(userMiner, now)
                  : {
                      dailyPreview: 0,
                      fractionDay: 0,
                      weeklyPreview: 0,
                      weeklyFraction: 0,
                    };

                // si está roto, el dailyPreview siempre 0
                const dailyPreviewSafe = isBroken ? 0 : preview.dailyPreview;
                const weeklyPreviewSafe = preview.weeklyPreview;

                const dailyGoal = Number(
                  (hasMiner &&
                    (userMiner.dailyGoal ?? userMiner.dailyProduction)) ??
                    cfg.dailyProduction ?? 0
                );
                const weeklyGoal = Number(
                  (hasMiner && (userMiner.weeklyGoal ?? dailyGoal * 7)) ??
                    dailyGoal * 7
                );

                const canRepair = hasMiner && isBroken; // solo se puede reparar si está roto

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={cfg.key}>
                    <Card
                      sx={{
                        position: "relative",
                        maxWidth: 200,
                        mx: "auto",
                        mt: 0.5,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "rgba(8,8,8,0.9)",
                        borderRadius: 2,
                        border: hasMiner
                          ? "1px solid rgba(255,213,79,0.9)"
                          : "1px solid rgba(255,255,255,0.12)",
                        boxShadow: hasMiner
                          ? "0 0 14px rgba(255,213,79,0.4)"
                          : "0 0 8px rgba(0,0,0,0.9)",
                        overflow: "visible",
                        transition:
                          "transform 0.25s ease, box-shadow 0.25s ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: "0 0 22px rgba(255,213,79,0.8)",
                        },
                        "&:hover .miner-image": {
                          transform: "scale(1.5)",
                          zIndex: 3,
                        },
                        ...(isMining && {
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: -2,
                            borderRadius: 2,
                            border: "2px solid rgba(255,213,79,0.6)",
                            pointerEvents: "none",
                            animation: "glowBorder 2s ease-in-out infinite",
                          },
                        }),
                        "@keyframes glowBorder": {
                          "0%": { boxShadow: "0 0 0 rgba(255,213,79,0.0)" },
                          "50%": {
                            boxShadow: "0 0 20px rgba(255,213,79,0.9)",
                          },
                          "100%": { boxShadow: "0 0 0 rgba(255,213,79,0.0)" },
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleOpenInfo(cfg)}
                        sx={{
                          flexGrow: 1,
                          pt: 1,
                          pb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={cfg.imageUrl}
                            alt={cfg.title}
                            className="miner-image"
                            sx={{
                              width: "60%",
                              height: "auto",
                              objectFit: "contain",
                              bgcolor: "rgba(0,0,0,0.85)",
                              filter: hasMiner
                                ? "none"
                                : "grayscale(1) brightness(0.4)",
                              opacity: hasMiner ? 1 : 0.85,
                              transition:
                                "transform 0.35s ease, filter 0.3s ease",
                              borderRadius: 1,
                              boxShadow: "0 0 10px rgba(0,0,0,0.8)",
                            }}
                          />

                          {/* chips arriba izquierda */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 6,
                              left: 6,
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Chip
                              size="small"
                              label={cfg.shortName}
                              sx={{
                                bgcolor: "rgba(0,0,0,0.75)",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.4)",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                            <Chip
                              size="small"
                              label={cfg.community}
                              sx={{
                                bgcolor: "rgba(0,0,0,0.6)",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.2)",
                                fontSize: "0.65rem",
                                height: 20,
                              }}
                            />
                          </Box>

                          {/* chips arriba derecha */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 0.5,
                            }}
                          >
                            {hasMiner ? (
                              <Chip
                                size="small"
                                label={
                                  isBroken
                                    ? "Broken"
                                    : isActive
                                    ? "Owned"
                                    : "Inactive"
                                }
                                sx={{
                                  bgcolor: isBroken
                                    ? "rgba(244,67,54,0.9)"
                                    : isActive
                                    ? "rgba(76,175,80,0.85)"
                                    : "rgba(158,158,158,0.85)",
                                  color: "#fff",
                                  fontWeight: 700,
                                  textShadow: "0 0 4px rgba(0,0,0,0.8)",
                                  fontSize: "0.7rem",
                                  height: 22,
                                }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="Not owned"
                                sx={{
                                  bgcolor: "rgba(97,97,97,0.8)",
                                  color: "#fff",
                                  fontSize: "0.7rem",
                                  height: 22,
                                }}
                              />
                            )}

                            {/* animated mining badge */}
                            {isMining && (
                              <Chip
                                size="small"
                                label="Mining..."
                                sx={{
                                  bgcolor: "rgba(255,213,79,0.15)",
                                  border: "1px solid rgba(255,213,79,0.9)",
                                  color: "#ffe082",
                                  fontSize: "0.65rem",
                                  height: 20,
                                  "@keyframes pulseMining": {
                                    "0%": {
                                      opacity: 0.4,
                                      transform: "scale(1)",
                                    },
                                    "50%": {
                                      opacity: 1,
                                      transform: "scale(1.05)",
                                    },
                                    "100%": {
                                      opacity: 0.4,
                                      transform: "scale(1)",
                                    },
                                  },
                                  animation:
                                    "pulseMining 1.6s ease-in-out infinite",
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        <CardContent
                          sx={{
                            pt: 0,
                            pb: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: "#fffde7",
                              textShadow: "0 0 4px rgba(0,0,0,0.9)",
                              fontSize: "0.9rem",
                            }}
                          >
                            {cfg.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255,255,255,0.8)",
                              fontSize: "0.78rem",
                            }}
                            noWrap
                          >
                            {cfg.minerDescription}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              mt: 0.5,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Tooltip title="Base daily VIPHueso production">
                              <Chip
                                size="small"
                                label={`${cfg.dailyProduction.toFixed(
                                  2
                                )} VIPH/day`}
                                sx={{
                                  bgcolor: "rgba(3,169,244,0.2)",
                                  border: "1px solid rgba(3,169,244,0.85)",
                                  color: "#E1F5FE",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: 22,
                                }}
                              />
                            </Tooltip>

                            <Tooltip title="Max per user">
                              <Chip
                                size="small"
                                label={`Max: ${cfg.maxPerUser}`}
                                sx={{
                                  bgcolor: "rgba(158,158,158,0.2)",
                                  border: "1px solid rgba(224,224,224,0.5)",
                                  color: "#FAFAFA",
                                  fontSize: "0.7rem",
                                  height: 22,
                                }}
                              />
                            </Tooltip>
                          </Stack>

                          {hasMiner && (
                            <Box sx={{ mt: 1 }}>
                              {/* Life */}
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.25 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#fff", fontSize: "0.7rem" }}
                                >
                                  Life
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#fff", fontSize: "0.7rem" }}
                                >
                                  {lifeCurrent} / {lifeMax}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={lifePercent}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: "rgba(255,255,255,0.1)",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    background: isBroken
                                      ? "linear-gradient(90deg,#f44336,#d32f2f)"
                                      : "linear-gradient(90deg,#ffeb3b,#ff9800)",
                                  },
                                }}
                              />

                              {/* Daily mining preview */}
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mt: 0.6 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(255,255,255,0.8)",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Today mined (preview):
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#BBDEFB",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {dailyGoal > 0
                                    ? `${dailyPreviewSafe.toFixed(
                                        4
                                      )} / ${dailyGoal.toFixed(2)} VIPH`
                                    : `${dailyPreviewSafe.toFixed(4)} VIPH`}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={isBroken ? 0 : preview.fractionDay * 100}
                                sx={{
                                  mt: 0.3,
                                  height: 5,
                                  borderRadius: 999,
                                  bgcolor: "rgba(255,255,255,0.08)",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    background:
                                      "linear-gradient(90deg,#4FC3F7,#29B6F6)",
                                  },
                                }}
                              />

                              {/* Weekly mining preview */}
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mt: 0.6 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(255,255,255,0.8)",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Weekly mined (preview):
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#fff59d",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {weeklyGoal > 0
                                    ? `${weeklyPreviewSafe.toFixed(
                                        3
                                      )} / ${weeklyGoal.toFixed(3)} VIPH`
                                    : `${weeklyPreviewSafe.toFixed(3)} VIPH`}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={preview.weeklyFraction * 100}
                                sx={{
                                  mt: 0.3,
                                  height: 5,
                                  borderRadius: 999,
                                  bgcolor: "rgba(255,255,255,0.08)",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    background:
                                      "linear-gradient(90deg,#AED581,#81C784)",
                                  },
                                }}
                              />
                            </Box>
                          )}

                          {!hasMiner && (
                            <Typography
                              variant="caption"
                              sx={{
                                mt: 1,
                                color: "rgba(255,255,255,0.7)",
                                fontSize: "0.7rem",
                              }}
                            >
                              You don&apos;t own this miner yet. Buy it in the
                              market to start mining.
                            </Typography>
                          )}
                        </CardContent>
                      </CardActionArea>

                      <CardActions
                        sx={{
                          px: 1.5,
                          pb: 1.2,
                          pt: 0.4,
                          justifyContent: "space-between",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => handleOpenInfo(cfg)}
                          sx={{
                            textTransform: "none",
                            color: "#fff",
                            fontSize: "0.75rem",
                          }}
                        >
                          More info
                        </Button>

                        {!hasMiner ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleBuyClick}
                            sx={{
                              textTransform: "none",
                              borderRadius: 999,
                              px: 2,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              bgcolor: "#ffb300",
                              "&:hover": { bgcolor: "#ffa000" },
                            }}
                          >
                            Buy
                          </Button>
                        ) : (
                          <Tooltip
                            title={
                              canRepair
                                ? "Repair this miner spending Hueso."
                                : "Miner is working normally."
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!canRepair || repairLoading}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canRepair) handleOpenRepair(cfg, userMiner);
                                }}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                  px: 2,
                                  fontSize: "0.75rem",
                                  color: canRepair
                                    ? "#fff9c4"
                                    : "rgba(255,255,255,0.7)",
                                  borderColor: canRepair
                                    ? "rgba(255,235,59,0.9)"
                                    : "rgba(255,235,59,0.6)",
                                }}
                              >
                                Repair
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>

      {/* REPAIR MODAL */}
      <Dialog
        open={repairOpen}
        onClose={handleCloseRepair}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#111",
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.2)",
          },
        }}
      >
        {repairCfg && repairUserMiner && (
          <>
            <DialogTitle
              sx={{
                color: "#ffecb3",
                fontWeight: 700,
                textShadow: "0 0 8px rgba(0,0,0,0.8)",
              }}
            >
              Repair {repairCfg.title}
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: "#333" }}>
              {repairError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5, bgcolor: "rgba(244,67,54,0.15)" }}
                >
                  {repairError}
                </Alert>
              )}

              {(() => {
                const lifeCurrent = Number(repairUserMiner.lifeCurrent || 0);
                const lifeMax = Number(repairUserMiner.lifeMax || 0);
                const missingLife = Math.max(0, lifeMax - lifeCurrent);
                const cost = repairPreviewHueso;
                const saldoAntes = displayUserHueso;
                const saldoDespues =
                  cost > 0 ? Math.max(0, saldoAntes - cost) : saldoAntes;

                return (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}
                    >
                      This miner is <strong>broken</strong>. Repairing it will
                      restore its life so it can start mining again.
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 0.5 }}
                    >
                      Life:{" "}
                      <strong>
                        {lifeCurrent} ➜ {lifeMax}
                      </strong>{" "}
                      ({missingLife} life to recover)
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 0.5 }}
                    >
                      Your balance:{" "}
                      <strong>{saldoAntes.toLocaleString()} Hueso</strong>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 1.5 }}
                    >
                      Repair cost:{" "}
                      <strong>
                        {cost > 0
                          ? `${cost.toLocaleString()} Hueso`
                          : "configured in backend"}
                      </strong>
                      {cost > 0 && (
                        <>
                          {" "}
                          · After repair:{" "}
                          <strong>
                            {saldoDespues.toLocaleString()} Hueso
                          </strong>
                        </>
                      )}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      The final cost is validated by the server. If you don&apos;t
                      have enough Hueso, the repair will be rejected.
                    </Typography>
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ p: 1.5 }}>
              <Button
                onClick={handleCloseRepair}
                disabled={repairLoading}
                sx={{ textTransform: "none", color: "#fff" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRepair}
                disabled={repairLoading}
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 700,
                  bgcolor: "#ffb300",
                  "&:hover": { bgcolor: "#ffa000" },
                }}
              >
                {repairLoading ? (
                  <CircularProgress size={18} sx={{ color: "#000" }} />
                ) : (
                  "Confirm repair"
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* INFO MODAL */}
      <Dialog
        open={infoOpen}
        onClose={handleCloseInfo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#111",
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.2)",
          },
        }}
      >
        {infoConfig && (
          <>
            <DialogTitle
              sx={{
                color: "#ffecb3",
                fontWeight: 700,
                textShadow: "0 0 8px rgba(0,0,0,0.8)",
              }}
            >
              {infoConfig.title} — {infoConfig.community}
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: "#333" }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "#fffde7", mb: 0.5 }}
              >
                Game / Community
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.85)", mb: 1.5 }}
              >
                {infoConfig.gameDescription}
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{ color: "#fffde7", mb: 0.5 }}
              >
                Miner role
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.85)", mb: 1.5 }}
              >
                {infoConfig.minerDescription}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1.5, flexWrap: "wrap" }}
              >
                <Chip
                  size="small"
                  label={`Template ID: ${infoConfig.templateId}`}
                  sx={{
                    bgcolor: "rgba(158,158,158,0.2)",
                    border: "1px solid rgba(224,224,224,0.4)",
                    color: "#fff",
                  }}
                />
                <Chip
                  size="small"
                  label={`Base life: ${infoConfig.baseLife}`}
                  sx={{
                    bgcolor: "rgba(3,169,244,0.18)",
                    border: "1px solid rgba(3,169,244,0.6)",
                    color: "#E1F5FE",
                  }}
                />
                <Chip
                  size="small"
                  label={`Daily: ${infoConfig.dailyProduction.toFixed(
                    2
                  )} VIPH`}
                  sx={{
                    bgcolor: "rgba(129,199,132,0.18)",
                    border: "1px solid rgba(129,199,132,0.7)",
                    color: "#E8F5E9",
                  }}
                />
                <Chip
                  size="small"
                  label={`Wear: ${infoConfig.wearMinPerDay}–${infoConfig.wearMaxPerDay}/day`}
                  sx={{
                    bgcolor: "rgba(244,81,30,0.2)",
                    border: "1px solid rgba(244,81,30,0.7)",
                    color: "#FFCCBC",
                  }}
                />
              </Stack>

              {infoUserMiner ? (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#fffde7", mb: 0.5 }}
                  >
                    Your miner status
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      mb: 0.5,
                    }}
                  >
                    Life:{" "}
                    <strong>
                      {infoUserMiner.lifeCurrent} / {infoUserMiner.lifeMax}
                    </strong>
                    {" · "}
                    Weekly mined:{" "}
                    <strong>
                      {Number(infoUserMiner.weeklyAccumulated || 0).toFixed(3)}{" "}
                      VIPH
                    </strong>
                    {infoUserMiner.active ? (
                      <Chip
                        size="small"
                        label="Active"
                        sx={{
                          ml: 1,
                          bgcolor: "rgba(76,175,80,0.9)",
                          color: "#fff",
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Inactive"
                        sx={{
                          ml: 1,
                          bgcolor: "rgba(244,67,54,0.9)",
                          color: "#fff",
                        }}
                      />
                    )}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    mb: 1.5,
                  }}
                >
                  You don&apos;t own this miner yet. Once you buy it in the
                  market, it will appear here with its own life, wear and weekly
                  mining progress.
                </Typography>
              )}

              <Typography
                variant="subtitle2"
                sx={{ color: "#fffde7", mb: 0.5 }}
              >
                Useful links
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                {infoConfig.links?.discord && (
                  <Button
                    variant="outlined"
                    size="small"
                    href={infoConfig.links.discord}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      borderColor: "rgba(129,212,250,0.8)",
                      color: "#B3E5FC",
                    }}
                  >
                    Discord
                  </Button>
                )}
                {infoConfig.links?.website && (
                  <Button
                    variant="outlined"
                    size="small"
                    href={infoConfig.links.website}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      borderColor: "rgba(255,213,79,0.8)",
                      color: "#FFF59D",
                    }}
                  >
                    Website
                  </Button>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 1.5 }}>
              {!infoUserMiner && (
                <Button
                  onClick={handleBuyClick}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    px: 3,
                    fontWeight: 700,
                    bgcolor: "#ffb300",
                    "&:hover": { bgcolor: "#ffa000" },
                  }}
                >
                  Go to market
                </Button>
              )}
              <Button
                onClick={handleCloseInfo}
                sx={{ textTransform: "none", color: "#fff" }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
